import { padUpcomingQueueToSlotCount } from '../courtUtils';

export function emptyLocalActiveCourt(courtNumber) {
  return {
    courtNumber,
    team1: [],
    team2: [],
    inProgress: false,
    winner: null,
    score: null,
    startTime: null,
    gameCreation: {
      origin: null,
      algorithm: null,
      isAltered: false,
      scheduledGameId: null,
    },
  };
}

export function findFirstActiveCourtGap(courtAssignments) {
  const numbers = (courtAssignments || [])
    .map((court) => court?.courtNumber)
    .filter((n) => n != null)
    .sort((a, b) => a - b);
  for (let i = 1; i <= numbers.length; i += 1) {
    if (numbers[i - 1] !== i) return i;
  }
  return null;
}

function highestCourtNumber(courtAssignments) {
  return (courtAssignments || []).reduce(
    (max, court) => Math.max(max, Number(court?.courtNumber) || 0),
    0
  );
}

/**
 * Pad or trim upcoming slots on this phone. Shrinking returns those players to the waitlist.
 */
export function applyLocalUpcomingSlotCount({
  nextAssignments = [],
  waitingList = [],
  newCount,
  playersById = {},
  createEmptySlot,
  requeueUnplayedPlayers,
}) {
  const current = Array.isArray(nextAssignments) ? nextAssignments : [];
  const wait = Array.isArray(waitingList) ? waitingList : [];
  const count = Number(newCount);
  if (!Number.isFinite(count) || count < 0) {
    return { nextAssignments: current, waitingList: wait };
  }

  if (current.length > count) {
    const excessPlayers = current
      .slice(count)
      .flatMap((match) => [...(match.team1 || []), ...(match.team2 || [])])
      .filter((id) => id && (!playersById || playersById[id]));
    const nextWait = typeof requeueUnplayedPlayers === 'function'
      ? requeueUnplayedPlayers(wait, excessPlayers)
      : [...wait, ...excessPlayers.filter((id) => !wait.includes(id))];
    return {
      nextAssignments: current.slice(0, count),
      waitingList: nextWait,
    };
  }

  if (count > current.length) {
    if (typeof createEmptySlot !== 'function') {
      return { nextAssignments: current, waitingList: wait };
    }
    return {
      nextAssignments: padUpcomingQueueToSlotCount(current, count, createEmptySlot),
      waitingList: wait,
    };
  }

  return { nextAssignments: current, waitingList: wait };
}

/**
 * Add or remove an active court using the same gap-fill / highest-empty rules as the live path.
 */
export function applyLocalActiveCourtCount({
  courtAssignments = [],
  currentCount,
  increment,
  maxCourts = 10,
}) {
  const courts = Array.isArray(courtAssignments) ? courtAssignments : [];
  const fromCount = currentCount != null ? Number(currentCount) : courts.length;
  const nextCount = Math.min(Math.max(1, fromCount + Number(increment || 0)), maxCourts);
  const inProgressCount = courts.filter((court) => court?.inProgress).length;

  if (nextCount < inProgressCount) {
    return {
      ok: false,
      reason: `Cannot reduce courts below the number of courts with games in progress (${inProgressCount}). Finish those games first.`,
    };
  }

  if (nextCount === fromCount) {
    return {
      ok: true,
      noChange: true,
      courtAssignments: courts,
      numberOfCourts: fromCount,
    };
  }

  if (nextCount > fromCount) {
    const next = [...courts];
    const courtNumbersAdded = [];
    const toAdd = nextCount - fromCount;
    for (let i = 0; i < toAdd; i += 1) {
      const courtNumber = findFirstActiveCourtGap(next) ?? (highestCourtNumber(next) + 1);
      if (!next.some((court) => Number(court.courtNumber) === courtNumber)) {
        next.push(emptyLocalActiveCourt(courtNumber));
        courtNumbersAdded.push(courtNumber);
      }
    }
    next.sort((a, b) => (a.courtNumber || 0) - (b.courtNumber || 0));
    return {
      ok: true,
      courtAssignments: next,
      numberOfCourts: nextCount,
      courtNumbersAdded,
    };
  }

  const sorted = [...courts].sort((a, b) => (a.courtNumber || 0) - (b.courtNumber || 0));
  const highest = sorted[sorted.length - 1];
  if (!highest) {
    return { ok: false, reason: 'No courts exist to remove' };
  }
  if (highest.inProgress === true) {
    return {
      ok: false,
      reason: `Court ${highest.courtNumber} has a game in progress. Finish the game first.`,
    };
  }
  const hasPlayers = (highest.team1?.length || 0) + (highest.team2?.length || 0) > 0;
  if (hasPlayers) {
    return {
      ok: false,
      reason: `Court ${highest.courtNumber} must be empty before reducing. Remove players first.`,
    };
  }
  if (fromCount - nextCount > 1) {
    return {
      ok: false,
      reason: 'Can only reduce one court at a time. The highest court must be empty and not in progress.',
    };
  }

  return {
    ok: true,
    courtAssignments: courts.filter((court) => court.courtNumber !== highest.courtNumber),
    numberOfCourts: nextCount,
    courtNumberRemoved: highest.courtNumber,
  };
}
